import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import api from '../../lib/api';

export default function AdaptationWidget() {
  const navigate = useNavigate();
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/adaptation-stats')
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <Skeleton className="h-4 w-56 mb-4" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const totalToHandle = (stats.awaiting_implementation || 0) + (stats.pending_decisions || 0);
  if (totalToHandle === 0 && stats.overdue === 0) return null;

  const awaiting  = stats.awaiting_implementation || 0;
  const overdue   = stats.overdue || 0;
  const completed = stats.completed || 0;
  const pending   = stats.pending_decisions || 0;

  const tiles = [
    {
      key:      'pending',
      label:    'Beslutade',
      value:    pending,
      border:   'border-slate-200',
      numCls:   'text-slate-800',
      lblCls:   'text-slate-500',
      icon:     null,
    },
    {
      key:      'awaiting',
      label:    'Väntar på genomförande',
      value:    awaiting,
      border:   awaiting > 0 ? 'border-yellow-200' : 'border-slate-200',
      numCls:   awaiting > 0 ? 'text-yellow-700'  : 'text-slate-800',
      lblCls:   awaiting > 0 ? 'text-yellow-600'  : 'text-slate-500',
      icon:     null,
    },
    {
      key:      'overdue',
      label:    'Försenade (>30d)',
      value:    overdue,
      border:   overdue > 0 ? 'border-red-200'    : 'border-slate-200',
      numCls:   overdue > 0 ? 'text-red-700'      : 'text-slate-800',
      lblCls:   overdue > 0 ? 'text-red-600'      : 'text-slate-500',
      icon:     overdue > 0 ? <AlertTriangle size={11} className="text-red-500 shrink-0" /> : null,
    },
    {
      key:      'completed',
      label:    'Genomförda',
      value:    completed,
      border:   completed > 0 ? 'border-green-200' : 'border-slate-200',
      numCls:   completed > 0 ? 'text-green-700'   : 'text-slate-800',
      lblCls:   completed > 0 ? 'text-green-600'   : 'text-slate-500',
      icon:     null,
    },
  ];

  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
            ANPASSNINGAR ATT HANTERA
          </p>
          <button
            className="text-xs text-slate-500 hover:text-slate-700 hover:underline flex items-center gap-1"
            onClick={() => navigate('/participants')}
          >
            Se alla <ArrowRight size={11} />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {tiles.map(t => (
            <div key={t.key} className={`p-4 rounded-lg border ${t.border}`}>
              <p className={`text-3xl font-bold ${t.numCls}`}>{t.value}</p>
              <p className={`text-xs mt-1 flex items-center gap-1 ${t.lblCls}`}>
                {t.icon}
                {t.label}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
